import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import {
  ChangeHotelResponse,
  ChangeTicketResponse,
  DynamicEventResponse,
  GrossAndTax,
} from 'ts/dynamic/interfaces/DynamicResponse';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DynamicSummary from './Summary';
import DynamicTicket from './Flight/DynamicTicket';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowCircleLeft,
  faBed,
  faPlane,
  faSearch,
  faTicket,
} from '@fortawesome/free-solid-svg-icons';
import DynamicHotel from './Hotel/DynamicHotel';
import DynamicAttraction from './Attraction/DynamicAttraction';
import ChangeFlight from './Flight/ChangeFlight';
import ChangeAttraction from './Attraction/ChangeAttraction';
import ChangeTransfer from './Transfer/ChangeTransfer';
import { initFlight } from 'utils/dynamic/dynamic-fl';
import { useTranslation } from 'react-i18next';
import Button from 'components/ui/Button/Button';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import DynamicFlightDetailsSkeleton from './DynamicFlightDetailsSkeleton';
import useRecaptcha from 'hooks/useRecaptcha';
import { saveScreenshot, showError, showWarning } from 'utils/utils';
import { changeHotel, getDetails, ohsForChangeHotel } from 'services/dynamic';
import { DynamicContext } from 'contexts/DynamicContext';
import ChangeHotel2 from './Hotel/ChangeHotel2';
import { initSingleHotel } from 'utils/dynamic/dynamic-htl';
import LoaderMain from 'components/shared/LoaderMain';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { getPaxCountOptions } from 'constants/dynamic';
import ChooseRoomModal from './Hotel/ChooseRoomModal';
import { Hotel } from 'ts/hotel/interfaces/HotelResponse';

interface DynamicDetailsProps {
  data: DynamicEventResponse;
  onChangeData: (data: DynamicEventResponse) => void;
  onUpdate: (str?: string) => void;
  onSuccessBook: () => void;
  onClickBack: () => void;
  onClickSearch: () => void;
}

const DynamicDetails: FC<DynamicDetailsProps> = ({ data, onChangeData, ...props }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isChangeFlight, setIsChangeFlight] = useState(false);
  const [isChangeHotel, setIsChangeHotel] = useState(false);
  const [isChangeAttraction, setIsChangeAttraction] = useState(false);
  const [bookLoading, setBookLoading] = useState(false);
  const [urlParams] = useSearchParams();
  const detailsRef = useRef<HTMLDivElement>(null);
  const { numberOfPax } = useContext(DynamicContext);
  const [isShownRoomsModal, setIsShownRoomsModal] = useState(false);
  const isTicketOnly = useRef(urlParams.get('matrixConfig') === 'Dynamic_AM').current;
  const [ohsHotel, setOHSHotel] = useState<Hotel | undefined>(undefined);
  const selectedRoom =
    data.dynamicHotel?.selectedRoom || data.dynamicHotel?.selectedHotel?.roomClasses[0];
  const { handleRecapcha } = useRecaptcha();
  const [ticketPaxCount, setTicketPaxCount] = useState(1);

  useEffect(() => {
    let ohsLoaded = sessionStorage.getItem('ohsLoaded');
    if (data && ohsLoaded !== data.searchToken) {
      sessionStorage.setItem('ohsLoaded', data.searchToken);
      ohsForChangeHotel(
        data.searchToken,
        data.dynamicHotel?.selectedHotel!,
        data.dynamicHotel?.selectedRoom
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeFlight = () => {
    setIsChangeFlight(false);

    onChangeData({
      ...data,
      dynamicFlight: { ...data.dynamicFlight!, selectedFlight: undefined },
      packageInfo: { ...data.packageInfo, packageTotalPrice: undefined },
    });

    props.onUpdate();
  };

  const onChangeHotel = (response: ChangeHotelResponse) => {
    setIsChangeHotel(false);

    if (data.dynamicHotel && response.hotelChanged) {
      const newInitedHotel = initSingleHotel(response.selectedHotel, urlParams);

      onChangeData({
        ...data,
        dynamicHotel: {
          ...data.dynamicHotel,
          selectedHotel: newInitedHotel,
          selectedRoom: response.selectedRoomClass,
          ohsCompletedAndReadyToGo: true,
        },
        packageInfo: {
          ...data.packageInfo,
          packageTotalPrice: response.packageTotalPrice,
        },
      });
    }
  };

  const onChangeTicket = (response: ChangeTicketResponse) => {
    setIsChangeAttraction(false);

    if (data) {
      const newDyn = data.dynamicAttraction?.tickets.find(
        (h) => h.ticket.itemNumber === response.id
      );

      if (newDyn) {
        const newAttraction = {
          id: newDyn.ticket.itemNumber,
          image: newDyn.ticket.pictureMap,
          showName: newDyn.ticket.showName,
          showId: newDyn.ticket.showId,
          vanueDesc: newDyn.ticket.vanueDesc,
          categoryDesc: newDyn.ticket.categoryDesc,
          ticketDesc: newDyn.ticket.ticketDesc,
          eventDate: newDyn.ticket.eventDate,
          price: newDyn.ticket.myPrice,
          numberOfPax: data.dynamicAttraction!.numberOfPax,
          city: newDyn.ticket.cityName,
          package: null,
          country: newDyn.ticket.countryName,
          shipping: newDyn.ticket.shipping,
          tickets: data.dynamicAttraction!.tickets,
          categoryImage: data.dynamicAttraction?.categoryImage,
        };
        onChangeData({
          ...data,
          dynamicAttraction: newAttraction,
          packageInfo: {
            ...data.packageInfo!,
            packageTotalPrice: response.packageTotalPrice,
          },
        });
      }
    }
  };

  const onClickBook = async (response?: ChangeHotelResponse) => {
    setBookLoading(true);
    if (response && response.hotelChanged) {
      onChangeData({
        ...data,
        dynamicHotel: {
          ...data.dynamicHotel!,
          selectedHotel: response.selectedHotel,
          selectedRoom: response.selectedRoomClass,
          ohsCompletedAndReadyToGo: true,
        },
        packageInfo: {
          ...data.packageInfo!,
          packageTotalPrice: response.packageTotalPrice,
        },
      });
    }
    if (
      data.dynamicHotel &&
      !data.dynamicHotel?.ohsCompletedAndReadyToGo &&
      (!response || response?.needToOhs)
    ) {
      const ohsResponse = await ohsForChangeHotel(
        data.searchToken,
        data.dynamicHotel?.selectedHotel!,
        selectedRoom
      );

      if (ohsResponse && !ohsResponse.hasErrors && !ohsResponse.oneHotelSearchFailed) {
        setIsShownRoomsModal(true);
        const htl = initSingleHotel(ohsResponse.hotelFromOneHotelSearch, urlParams);
        setOHSHotel(htl);
      } else if (ohsResponse && ohsResponse.oneHotelSearchFailed) {
        showWarning(t('dyn.there_is_no_any_room_please_choose_another_hotel'));
      } else {
        showError(ohsResponse?.errorMessage || t('something_went_wrong'));
      }
      setBookLoading(false);
      return;
    }

    const token = await handleRecapcha();
    if (!token) return;

    saveScreenshot(detailsRef.current, 'details', 'Dynamic');
    const book = await getDetails(token, data.searchToken);
    setBookLoading(false);

    if (book?.bookingModel?.oneHotelSearchFailed) {
      showError(t('hotel.one_hotel_search_failed'));

      const token = await handleRecapcha();
      if (!token || !data.dynamicHotel?.selectedHotel || !data.dynamicHotel?.selectedRoom) return;

      await changeHotel(
        token,
        data.searchToken,
        data.dynamicHotel?.selectedHotel,
        data.dynamicHotel?.selectedRoom
      );
      onChangeData(data);
    } else if (!book?.hasErrors) {
      const newBook: any = { ...book };
      if (newBook.bookingModel?.hotelInfo?.hotel) {
        newBook.bookingModel.hotelInfo.hotel.searchToken = data.dynamicHotel?.searchToken;
      }
      sessionStorage.setItem(
        'detailsDynamic' + urlParams.get('matrixConfig') + urlParams.get('searchOption'),
        JSON.stringify({ ...book })
      );
      let url = `/dynamic/book?searchToken=${data.searchToken}&matrixConfig=${urlParams.get(
        'matrixConfig'
      )}${data.dynamicFlight ? `&flightToken=${data.dynamicFlight.searchToken}` : ''}`;
      if (isTicketOnly) {
        url += `&ticketPaxCount=${ticketPaxCount}`;
        url += `&searchOption=${urlParams.get('searchOption')}`;
      }
      props.onSuccessBook();
      navigate(url);
    } else {
      showError(book?.errorMessage || 'Error');
    }
  };

  return (
    <div ref={detailsRef}>
      {!isChangeFlight && !isChangeHotel && !isChangeAttraction && (
        <div className="details-wrapper" style={{ minHeight: 'unset', paddingBottom: 0 }}>
          <div className="default-shadow flex-between dyn-change-header w100p">
            <Button
              title={t('back')}
              icon={faArrowCircleLeft}
              onClick={props.onClickBack}
              className="btn btn-link"
            />
            <Button
              title={t('search.new')}
              icon={faSearch}
              onClick={props.onClickSearch}
              className="btn btn-link"
            />
          </div>
        </div>
      )}
      {isChangeHotel && (
        <ChangeHotel2
          data={data}
          onSelectNewHotel={onChangeHotel}
          onPressBack={() => setIsChangeHotel(false)}
        />
      )}
      <div className={isChangeHotel ? '' : 'details-wrapper'}>
        {isChangeFlight && !!data.dynamicFlight && (
          <ChangeFlight
            flights={data.dynamicFlight.allFlights}
            selectedFlight={data.dynamicFlight.selectedFlight!}
            onSelectNewFlight={onChangeFlight}
            onPressBack={() => setIsChangeFlight(false)}
            searchToken={data.searchToken}
            currency={data.packageInfo.currencyCode.toLowerCase() as keyof GrossAndTax}
          />
        )}
        {isChangeAttraction && !isTicketOnly && data.dynamicAttraction && (
          <ChangeAttraction
            onPressBack={() => setIsChangeAttraction(false)}
            dynamicAttraction={data.dynamicAttraction}
            searchToken={data.searchToken}
            onSelectNewTicket={onChangeTicket}
            currency={(data.packageInfo.currencyCode.toLowerCase() || 'usd') as keyof GrossAndTax}
          />
        )}
        {isTicketOnly && data.dynamicAttraction && (
          <div
            className="display-flex flex-between ticket-only-wrapper"
            style={{ alignItems: 'flex-start' }}
          >
            <div className="details">
              <ChangeAttraction
                onPressBack={() => setIsChangeAttraction(false)}
                dynamicAttraction={data.dynamicAttraction}
                searchToken={data.searchToken}
                onSelectNewTicket={onChangeTicket}
                currency={
                  (sessionStorage.getItem('searchAttractionCurrency')?.toLowerCase() ||
                    'usd') as keyof GrossAndTax
                }
                isTicketOnly
              />

              <div className="flex-row-end mb-20">
                <div className="w200px mr-5">
                  <SelectCustom
                    options={getPaxCountOptions(data.dynamicAttraction.tickets[0].ticket.qnt)}
                    value={ticketPaxCount}
                    onChange={setTicketPaxCount}
                    movablePlaceholder={t('group.number_of_passengers')}
                    menuPlacement="top"
                  />
                </div>
                <div className="dyn-details-order" style={{ margin: 0 }}>
                  <Button
                    onClick={() => onClickBook()}
                    disabled={bookLoading}
                    title={bookLoading ? t('hotel.checking_room_availability') : t('dyn.to_order')}
                    className={'btn btn-lg btn-primary' + (bookLoading ? ' btn-pulse' : '')}
                  />
                </div>
              </div>
            </div>

            <DynamicSummary data={data} />
          </div>
        )}

        {!isChangeFlight && !isChangeHotel && !isChangeAttraction && !isTicketOnly && (
          <Fragment>
            <div className="details">
              {data.dynamicFlight?.selectedFlight && (
                <div className="default-shadow details-block">
                  <div className="title-header-grey title">
                    <FontAwesomeIcon icon={faPlane} className="mr-5" /> {t('flight.details')}
                  </div>
                  <DynamicTicket
                    dynamicFlight={initFlight(data.dynamicFlight.selectedFlight)}
                    onClickChange={() => setIsChangeFlight(true)}
                    onChangeFare={(total, selectedFlight) =>
                      onChangeData({
                        ...data,
                        packageInfo: { ...data.packageInfo, packageTotalPrice: total },
                        dynamicFlight: {
                          ...data.dynamicFlight!,
                          selectedFlight,
                        },
                      })
                    }
                    btnText={t('change_choice')}
                    mode={0}
                    hideShadow={true}
                    flexFlights={data.isFlexFlight ? data.flexDetails?.listOfFlexFlights : []}
                  />
                </div>
              )}

              {data.dynamicFlight && !data.dynamicFlight.selectedFlight && (
                <DynamicFlightDetailsSkeleton />
              )}

              {data.dynamicHotel && (
                <div className="default-shadow details-block">
                  <div className="title-header-grey title">
                    <FontAwesomeIcon icon={faBed} className="mr-5" /> {t('hotel.hotel')}
                  </div>
                  <DynamicHotel
                    numberOfPax={numberOfPax}
                    hotel={data.dynamicHotel.selectedHotel}
                    onClickChange={() => setIsChangeHotel(true)}
                    selectedRoom={data.dynamicHotel.selectedRoom}
                    searchToken={data.dynamicHotel.searchToken}
                  />
                </div>
              )}

              {data.dynamicAttraction && (
                <div className="default-shadow details-block">
                  <div className="title-header-grey title">
                    <FontAwesomeIcon icon={faTicket} className="mr-5" /> {t('dyn.ticket')}
                  </div>
                  <DynamicAttraction
                    ticket={data.dynamicAttraction}
                    onClickChange={() => setIsChangeAttraction(true)}
                  />
                </div>
              )}
              {data.transferIsUpsell && <ChangeTransfer data={data} onChange={onChangeData} />}
              <div className="dyn-details-order">
                <Button
                  onClick={() => onClickBook()}
                  disabled={bookLoading}
                  title={t('dyn.to_order')}
                  className={'btn btn-lg btn-primary' + (bookLoading ? ' btn-pulse' : '')}
                />
              </div>
            </div>
            <DynamicSummary data={data} />
          </Fragment>
        )}
      </div>
      
        {bookLoading && (
          <LoaderMain systemProducts="Dynamic" systemProductsDetails="LoadingDetails" />
        )}
      
      {isShownRoomsModal && ohsHotel && (
        <ChooseRoomModal
          roomClasses={ohsHotel?.roomClasses || []}
          choosenRoomPrice={
            ((selectedRoom as any).price[data.packageInfo.currencyCode.toLowerCase()] || 0) /
              numberOfPax || 0
          }
          numberOfPax={numberOfPax}
          selectedRoom={selectedRoom}
          onSelectNewHotel={(res) => {
            setIsShownRoomsModal(false);
            onClickBook(res);
          }}
          onCloseSuccess={() => setIsShownRoomsModal(false)}
          isLoading={false}
          hotel={ohsHotel}
          onClose={() => setIsShownRoomsModal(false)}
          searchToken={data.searchToken}
        />
      )}
    </div>
  );
};

export default withErrorBoundary(DynamicDetails, 'DynamicDetails');
